import { SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Input, Table, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { MainContent } from '../../components/MainContent';
import type { LabsQuery } from '../../graphql/generated/graphql.ts';
import { tableLinkHoverRow } from '../../styles/globalCss';
import { deCollator } from '../../utils/collator';
import { LABS_QUERY } from './Labs.gql';

const { useToken } = theme;

export type Lab = NonNullable<LabsQuery['labs']>[number];

const sortBy = (key: keyof Pick<Lab, 'name' | 'shortName' | 'longName'>) => (a: Lab, b: Lab) =>
  deCollator.compare(a[key] ?? '', b[key] ?? '');

export const Labs: React.FC = () => {
  const { token } = useToken();
  const navigate = useNavigate();
  const { data, loading } = useQuery(LABS_QUERY);

  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const applySearch = useDebouncedCallback((v: string) => setAppliedSearch(v), 300);

  const filtered = useMemo(
    () => filterByTokens(data?.labs ?? [], appliedSearch, [l => l.name, l => l.shortName, l => l.longName]),
    [data?.labs, appliedSearch]
  );

  const columns: ColumnsType<Lab> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: sortBy('name'),
      defaultSortOrder: 'ascend',
      render: value => <Markable tokens={searchValue}>{value}</Markable>,
    },
    {
      title: 'Kurzbezeichnung',
      dataIndex: 'shortName',
      key: 'shortName',
      ellipsis: true,
      width: '150px',
      sorter: sortBy('shortName'),
      render: value => <Markable tokens={searchValue}>{value}</Markable>,
    },
    {
      title: 'Langbezeichnung',
      dataIndex: 'longName',
      key: 'longName',
      ellipsis: true,
      sorter: sortBy('longName'),
      render: value => <Markable tokens={searchValue}>{value}</Markable>,
    },
  ];

  return (
    <MainContent>
      <PageHeader
        title="Labors"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          <Input
            allowClear
            placeholder="Suchen…"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={e => {
              setSearchValue(e.target.value);
              applySearch(e.target.value);
            }}
            style={{ width: 300 }}
          />
        }
      />
      <Table<Lab>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={filtered}
        pagination={false}
        loading={loading}
        columns={columns}
        rowClassName={tableLinkHoverRow(token.colorPrimary)}
        onRow={record => {
          return {
            onClick: () => navigate(`/admin/labors/${record.id}`),
          };
        }}
      />
    </MainContent>
  );
};
